import { useEffect } from "react";

import LandingNav from "@/components/landing/Nav";
import Hero from "@/components/landing/Hero";
import TradesStrip from "@/components/landing/TradesStrip";
import FeaturesGrid from "@/components/landing/FeaturesGrid";
import ProductShowcase from "@/components/landing/ProductShowcase";
import PricingPreview from "@/components/landing/PricingPreview";
import FAQ from "@/components/landing/FAQ";
import FinalCTA from "@/components/landing/FinalCTA";
import LandingFooter from "@/components/landing/LandingFooter";

/**
 * JobsBook.app — public marketing landing.
 *
 * Structure (top → bottom):
 *   Nav → Hero → TradesStrip (social proof) → FeaturesGrid
 *     → ProductShowcase (3 panels) → PricingPreview → FAQ
 *     → FinalCTA → Footer
 *
 * All colors from existing tokens (--primary slate, --accent amber).
 * No new fonts. All visuals are DIV-based mockups — zero image payload.
 *
 * SEO: sets <title> and <meta name="description"> via effect (no
 * react-helmet dependency needed for a single public page).
 */
export default function Landing() {
  useEffect(() => {
    document.title = "JobsBook — Bookings & invoicing for field service crews";
    const desc = "The operations OS for pump hire, plumbing, HVAC and mobile trades. Bookings, staff scheduling, and invoicing (VAT · RCT · cash) — from €29/month. 7-day free trial, no card.";
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", desc);

    // OG tags — set once so previews on WhatsApp/Slack render sensibly.
    const setOG = (property, content) => {
      let m = document.querySelector(`meta[property="${property}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("property", property);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };
    setOG("og:title", "JobsBook — Bookings & invoicing for field service crews");
    setOG("og:description", desc);
    setOG("og:type", "website");
    setOG("og:site_name", "JobsBook");
  }, []);

  // Set up an IntersectionObserver-based reveal on scroll. Elements with
  // class `reveal` fade + rise into view once, then stay put. No JS
  // libraries required.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const nodes = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("revealed");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      <LandingNav />
      <main>
        <Hero />
        <TradesStrip />
        <FeaturesGrid />
        <ProductShowcase />
        <PricingPreview />
        <FAQ />
        <FinalCTA />
      </main>
      <LandingFooter />
    </div>
  );
}
